import { useEffect, useRef, useState } from 'react'

import styled from 'styled-components'

interface ResizerProps {
  onResize: (width: number) => void
  minWidth?: number
  maxWidth?: number
  direction?: 'left' | 'right',
  modeType?: 'thin' | 'normal'
}

interface SResizerAreaProps {
  $direction: 'left' | 'right',
  $modeType: 'thin' | 'normal'
}

interface SResizerLineProps {
  direction: 'left' | 'right'
}



export const Resizer = ({ onResize, minWidth = 220, maxWidth = window.innerWidth * 0.5, direction = 'right', modeType = 'normal'  }: ResizerProps) => {
  const [isResizing, setIsResizing] = useState(false)
  const widthRef = useRef<number | null>(null)
  const frameRef = useRef<number | null>(null)

  useEffect(() => {
    if (isResizing) {
      const overlay = document.createElement('div')

      overlay.className = 'resizer-overlay'
      overlay.style.position = 'fixed'
      overlay.style.top = '0'
      overlay.style.left = '0'
      overlay.style.width = '100%'
      overlay.style.height = '100%'
      overlay.style.zIndex = '9999'
      overlay.style.cursor = 'col-resize'
      overlay.style.background = 'transparent'

      document.body.appendChild(overlay)

      return () => {
        document.body.removeChild(overlay)
      }
    }

    return undefined
  }, [isResizing])

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return

      const newWidth = direction === 'right'
        ? window.innerWidth - e.clientX
        : e.clientX

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        widthRef.current = newWidth

        if (frameRef.current) {
          cancelAnimationFrame(frameRef.current)
        }

        frameRef.current = requestAnimationFrame(() => {
          if (widthRef.current !== null) {
            onResize(widthRef.current)
          }
        })
      }
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      widthRef.current = null

      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }

      document.body.style.cursor = 'default'
      document.body.style.userSelect = 'auto'
    }

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)

      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }

      document.body.style.cursor = 'default'
      document.body.style.userSelect = 'auto'
    }
  }, [isResizing, minWidth, maxWidth, onResize, direction])

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }

  return (
    <SResizerArea
      $modeType={modeType}
      onMouseDown={handleMouseDown}
      className={isResizing ? 'active' : ''}
      $direction={direction}
    >
      <SResizerLine className={isResizing ? 'active' : ''}
        direction={direction}
      />
    </SResizerArea>
  )
}

const SResizerArea = styled.div<SResizerAreaProps>`
  width: ${props => props.$modeType === 'thin' ? '5px' : '20px'};
  height: 100%;
  position: absolute;
  ${props => props.$direction === 'right' ? `left: ${props.$modeType === 'thin' ? '-1px' : '-10px'};` : 'right: 0;'}
  top: 0;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`

const SResizerLine = styled.div<SResizerLineProps>`
  width: 2px;
  height: 100%;
  background-color: transparent;
  transition: background-color 0.2s;
  position: absolute;
  ${props => props.direction === 'right' 
    ? 'left: 50%; transform: translateX(-50%);' 
    : 'right: 0;'
}

  ${SResizerArea}:hover &, &.active {
    background-color: var(--color-primary);
  }
`
